import { Immutable } from "immer";
import React, { useContext } from "react";
import { getSpacingClasses } from "tsi-common-react/src/components/_spacing/spacing";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";

import { Context } from "@reactivated";
import type { HardcodedBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    PosturepedicEliteHybrid,
    PosturepedicEliteSpring,
    PosturepedicSpringHybridStandard,
} from "./charts";

import chartStyles from "./charts.module.scss";
import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

// Version choices that dispatch to a posturepedic comparison chart. Every other
// version renders the award badge (the legacy `{% file_exists %}` gate fell
// back to `award-badge.html` for any version without its own partial, and no
// award-badge version has one).
const POSTUREPEDIC_ELITE_HYBRID = "hc-block--posturepedic-elite-hybrid";
const POSTUREPEDIC_ELITE_SPRING = "hc-block--posturepedic-elite-spring";
const POSTUREPEDIC_SPRING_HYBRID_STANDARD =
    "hc-block--posturepedic-spring-hybrid-standard";

/**
 * Reads the reactivated `STATIC_URL` so the award-badge image URL can be
 * rebuilt the way the legacy `{% static %}` tag resolved it (matching the
 * toolboxComponentsMap `useStaticURL` precedent).
 */
const useStaticURL = (): string => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

/**
 * Award-badge port of the deleted `cms/blocks/hc-block/award-badge.html`. The
 * `version` doubles as the badge modifier class and the image filename (the
 * legacy `{{ template_name }}`). The BrandSpark body renders only for the
 * `circle` variants (`'circle' in template_name`).
 */
const AwardBadge = (
    props: Immutable<{ version: string; staticURL: string }>,
): React.JSX.Element => {
    const { version, staticURL } = props;
    return (
        <section
            className={concatClassNames([styles.brandsparkBadge, version])}
        >
            <div>
                <img
                    src={`${staticURL}img/hardcoded/${version}.png`}
                    alt="America's #1 Mattress Brand"
                    loading="lazy"
                    decoding="async"
                />
                <span className="ada-screenreader-only">
                    America&apos;s #1 Mattress Brand
                </span>
            </div>
            {version.includes("circle") && (
                <div className={styles.awardBadgeBody}>
                    <h1>Voted #1 Most Trusted Mattress Brand in America</h1>
                    <h2>2025 BrandSpark® American Trust Study</h2>
                </div>
            )}
        </section>
    );
};

/**
 * React port of the sealy `HardcodedBlock`.
 *
 * The block dispatches on the typed `version` field: award-badge versions
 * render the static badge markup; the three posturepedic-chart versions render
 * their static comparison tables (with the price row composed from the
 * server-serialized per-slot product JSON). The `<section class="hc-block ...">`
 * wrapper is rebuilt from the typed fields (same class order: version,
 * responsive visibility, spacing) around whichever inner content the version
 * selects.
 */
export const HardcodedBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const staticURL = useStaticURL();
    const products = v.chart_products;
    let inner: React.JSX.Element;
    // Chart module root: the legacy `.hc-block--posturepedic-*` ancestor was
    // this section, so the chart rules re-root on it.
    let chartRoot = "";
    switch (v.version) {
        case POSTUREPEDIC_ELITE_HYBRID:
            inner = <PosturepedicEliteHybrid products={products} />;
            chartRoot = chartStyles.posturepedicEliteHybrid;
            break;
        case POSTUREPEDIC_ELITE_SPRING:
            inner = <PosturepedicEliteSpring products={products} />;
            chartRoot = chartStyles.posturepedicEliteSpring;
            break;
        case POSTUREPEDIC_SPRING_HYBRID_STANDARD:
            inner = <PosturepedicSpringHybridStandard products={products} />;
            chartRoot = chartStyles.posturepedicSpringHybridStandard;
            break;
        default:
            inner = (
                <AwardBadge version={v.version ?? ""} staticURL={staticURL} />
            );
    }
    return (
        <section
            className={concatClassNames([
                "hc-block",
                styles.hardcodedBlock,
                v.version ?? "",
                chartRoot,
                v.responsive_visibility?.visibility ?? "",
                v.spacing ? getSpacingClasses(v.spacing) : "",
            ])}
        >
            {inner}
        </section>
    );
};
